import { el } from '../lib/ui.js';
import { parentTitle, pedigreeLayout, PEDIGREE_H, PEDIGREE_W, type Ancestor } from '../lib/family-pedigree.js';
import type { AtlasPerson } from '../lib/family-atlas.js';

type Options = {
  tree: Ancestor; card: (p: AtlasPerson) => HTMLButtonElement;
  explore: (id: number) => void; addParent: (child: number) => void;
  mode: string; onMode: (value: string) => void;
};

/** Due composizioni dello stesso albero: schema completo e rami leggibili.
 * Il cambio di dimensioni non ricrea form, selezione o dettagli aperti. */
export function renderPedigree(options: Options): { element: HTMLElement; dispose: () => void } {
  const { tree } = options;
  const plan = pedigreeLayout(tree);
  const byKey = new Map(plan.nodes.map((node) => [node.key, node]));
  const mode = el('select', { id: 'pedigree-mode', 'aria-label': 'Visualizzazione dei rami' },
    el('option', { value: 'auto' }, 'Adatta al dispositivo'), el('option', { value: 'diagram' }, 'Schema genealogico'), el('option', { value: 'list' }, 'Rami leggibili'));
  mode.value = options.mode;
  const host = el('section', { class: 'pedigree', 'data-mode': options.mode, 'aria-label': `Ascendenti di ${tree.person!.name}` });
  const control = (label: string, action: () => void): HTMLButtonElement => el('button', { type: 'button', class: 'atlas-button', click: action }, label);
  function entry(node: Ancestor): HTMLElement {
    const child = node.childId === null ? null : plan.nodes.find((p) => p.person?.id === node.childId)?.person;
    const title = `${parentTitle(node.role)}${child ? ` di ${child.name}` : ''}`;
    const wrapper = el('div', { class: `pedigree-node ${!node.person ? 'is-missing' : ''} ${node.proposed ? 'is-proposed' : ''}`, 'data-ancestor': node.key, 'data-side': node.side, 'data-level': String(node.level) }, el('p', { class: 'pedigree-role' }, title));
    if (!node.person) {
      wrapper.append(el('p', { class: 'pedigree-missing' }, 'Non ancora registrato'),
        node.childId !== null && node.childId > 0 && child?.status === 'confirmed' ? control('Collega un genitore', () => options.addParent(node.childId!)) : el('p', { class: 'atlas-hint' }, 'Crea o conferma prima la scheda della persona.'));
      return wrapper;
    }
    const p = node.person, card = options.card(p);
    card.classList.add('pedigree-person');
    wrapper.append(card);
    const notes = [node.cycle ? 'Legame circolare: da controllare' : node.repeated ? 'Presente anche in un altro ramo' : node.more ? 'Altri ascendenti: esplora da qui' : '', node.proposed ? 'Percorso da confermare' : ''].filter(Boolean);
    if (notes.length) wrapper.append(el('p', { class: 'pedigree-note', title: notes.join(' · ') }, notes.join(' · ')));
    if (node.role !== 'self') wrapper.append(control('Esplora i suoi rami', () => options.explore(p.id)));
    return wrapper;
  }
  const list = el('div', { class: 'pedigree-list' }, entry(tree));
  const nested = (node: Ancestor): HTMLElement => {
    const box = el('div', { class: 'pedigree-nested' }, entry(node));
    if (node.parents.length) {
      const children = el('div', { class: 'pedigree-parent-list' }, ...node.parents.map(nested));
      const details = el('details', { class: 'pedigree-ancestors', open: '' }, el('summary', {}, `I genitori di ${node.person!.name}`), children);
      box.append(details);
    }
    return box;
  };
  list.append(el('div', { class: 'pedigree-sides' }, ...tree.parents.map((node) => el('section', { class: 'pedigree-side', 'data-side': node.side, 'aria-label': node.role === 'father' ? `Ramo paterno${node.person ? ` · ${node.person.name}` : ''}` : node.role === 'mother' ? `Ramo materno${node.person ? ` · ${node.person.name}` : ''}` : `Ramo di ${node.person?.name ?? 'un altro genitore'}` },
    el('h3', {}, node.role === 'father' ? 'Ramo paterno' : node.role === 'mother' ? 'Ramo materno' : 'Ramo del genitore'), nested(node)))));
  const surface = el('div', { class: 'pedigree-surface', style: `width:${plan.width}px;height:${plan.height}px` });
  const scaled = el('div', { class: 'pedigree-scaled' }, surface);
  const viewport = el('div', { class: 'pedigree-viewport', tabindex: '0', role: 'region', 'aria-label': 'Schema genealogico. Ascendenti in alto, persona di partenza in basso. Scorri con le frecce o usa lo zoom.' }, scaled);
  const ns = 'http://www.w3.org/2000/svg', svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', `0 0 ${plan.width} ${plan.height}`); svg.classList.add('pedigree-lines'); svg.setAttribute('aria-hidden', 'true');
  for (const node of plan.nodes) {
    const child = plan.positions.get(node.key)!;
    for (const parent of node.parents) {
      const point = plan.positions.get(parent.key)!;
      const x1 = point.x + PEDIGREE_W / 2, y1 = point.y + PEDIGREE_H, x2 = child.x + PEDIGREE_W / 2, y2 = child.y;
      const edge = document.createElementNS(ns, 'path');
      edge.setAttribute('d', `M${x1},${y1} V${y1 + (y2 - y1) / 2} H${x2} V${y2}`);
      edge.setAttribute('class', `${parent.side} ${!parent.person || parent.proposed ? 'uncertain' : ''}`); svg.append(edge);
    }
  }
  surface.append(svg);
  for (const node of plan.nodes) {
    const point = plan.positions.get(node.key)!, card = entry(node);
    card.style.cssText = `left:${point.x}px;top:${point.y}px;width:${PEDIGREE_W}px;height:${PEDIGREE_H}px`;
    surface.append(card);
  }
  let zoom = 1, fitting = true, disposed = false;
  const output = el('output', { 'aria-live': 'polite', 'aria-label': 'Ingrandimento albero' }, '100%');
  function apply(next: number): void {
    zoom = Math.max(.01, Math.min(1.75, next));
    surface.style.transform = `scale(${zoom})`; scaled.style.width = `${plan.width * zoom}px`; scaled.style.height = `${plan.height * zoom}px`;
    output.textContent = `${Math.round(zoom * 100)}%`;
  }
  function fit(): void {
    if (!viewport.clientWidth || !viewport.clientHeight || disposed) return;
    fitting = true; apply(Math.min(1, (viewport.clientWidth - 16) / plan.width, (viewport.clientHeight - 16) / plan.height)); viewport.scrollTo(0, 0);
  }
  function changeZoom(factor: number): void {
    fitting = false;
    const cx = (viewport.scrollLeft + viewport.clientWidth / 2 - scaled.offsetLeft) / zoom, cy = (viewport.scrollTop + viewport.clientHeight / 2) / zoom;
    apply(zoom * factor); viewport.scrollTo(cx * zoom + scaled.offsetLeft - viewport.clientWidth / 2, cy * zoom - viewport.clientHeight / 2);
  }
  function centerRoot(): void {
    fitting = false; apply(Math.max(1, parseFloat(getComputedStyle(document.documentElement).fontSize) / 20)); const root = plan.positions.get(tree.key)!;
    viewport.scrollTo((root.x + PEDIGREE_W / 2) * zoom + scaled.offsetLeft - viewport.clientWidth / 2, (root.y + PEDIGREE_H / 2) * zoom - viewport.clientHeight / 2);
  }
  const diagram = el('div', { class: 'pedigree-diagram' },
    el('div', { class: 'pedigree-zoom' }, control('Riduci albero', () => changeZoom(1 / 1.25)), output, control('Ingrandisci albero', () => changeZoom(1.25)), control('Adatta i rami', fit), control('Leggi da vicino', centerRoot)), viewport,
    el('p', { class: 'atlas-hint' }, 'Padre a sinistra e madre a destra a ogni biforcazione, quando il dato è registrato. I riquadri vuoti non sono persone aggiunte all’archivio.'));
  host.append(el('label', { class: 'pedigree-mode', for: mode.id }, 'Come vuoi leggere l’albero?', mode), diagram, list);
  mode.addEventListener('change', () => { options.onMode(mode.value); host.dataset['mode'] = mode.value; fit(); });
  const observer = new ResizeObserver(() => { if (fitting) fit(); }); observer.observe(viewport);
  // I pulsanti fuori inquadratura diventano raggiungibili anche con la tastiera.
  surface.addEventListener('focusin', (event) => {
    const node = (event.target as HTMLElement).closest<HTMLElement>('[data-ancestor]');
    if (!node || !byKey.has(node.dataset['ancestor']!)) return;
    const rect = node.getBoundingClientRect(), frame = viewport.getBoundingClientRect();
    if (rect.left < frame.left || rect.right > frame.right || rect.top < frame.top || rect.bottom > frame.bottom) node.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  });
  requestAnimationFrame(() => { if (!disposed) fit(); });
  return { element: host, dispose: () => { disposed = true; observer.disconnect(); } };
}
